import { ResizeMode, Video } from 'expo-av';
import { useKeepAwake } from 'expo-keep-awake';
import { buildHomeBandWeightAndColor, IStrengthRecommendation } from 'fitbeat.gym-core';
import React, { useState } from 'react';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';
import theme from '../theme';
import { defaultScale, scaleText } from '../utils/scaling';
import { useGetVideoUrl } from './Hooks/useGetVideoUrl';
import { usePauseVideoOnNavigation } from './Hooks/usePauseVideoOnNavigation';
import { useSetupAudioMode } from './Hooks/useSetupAudio';
import { useShowCustomPlayButton } from './Hooks/useShowCustomPlayButton';
import VideoPlayBackButton from './VideoPlayBackButton';

interface IProps {
  videoName: string;
  recommendation?: IStrengthRecommendation;
  navigation: any;
}

export const ExplainExercise: React.FC<IProps> = ({
  navigation,
  videoName,
  recommendation,
}) => {
  useKeepAwake();
  const [isSettingUpAudio] = useSetupAudioMode();
  const videoRef = React.useRef<any>(null);
  const videoUrl = useGetVideoUrl(videoName);
  usePauseVideoOnNavigation(navigation, videoRef);
  const {
    onPlayButtonPressed,
    onVideoFinishedPlaying,
    shouldNativeControlsBeVisible,
    showPlayButton,
  } = useShowCustomPlayButton(videoRef);
  const [isVideoLoading, setIsVideoLoading] = useState(true);

  const handlePlaybackStatusUpdate = async (playbackStatus) => {
    if (playbackStatus.didJustFinish) {
      if (videoRef.current) {
        await videoRef.current.setPositionAsync(0);
        await videoRef.current.pauseAsync();
        onVideoFinishedPlaying();
      }
    }
  };

  const showLoadingIndicator = !videoUrl || isVideoLoading || isSettingUpAudio;
  const showVideo = !!videoUrl;
  const startingWeightAndColor = buildHomeBandWeightAndColor(recommendation?.suggestedStartingWeight || 'MEDIUM');
  const isPlayButtonVisible = showPlayButton && !showLoadingIndicator;
  return (
    <View style={styles.container}>
      <View style={styles.videoContainer}>
        {isPlayButtonVisible && (
          <VideoPlayBackButton onPress={onPlayButtonPressed} />
        )}
        <View style={styles.loadingContainer}>
          {showLoadingIndicator && (
            <ActivityIndicator
              size='small'
              color={theme.PRIMARY}
              animating={true}
            />
          )}
        </View>
        {showVideo && (
          <Video
            ref={videoRef}
            style={styles.video}
            source={{
              uri: videoUrl,
            }}
            useNativeControls={shouldNativeControlsBeVisible}
            resizeMode={ResizeMode.CONTAIN}
            onLoad={() => {
              setIsVideoLoading(false);
            }}
            onLoadStart={() => {
              setIsVideoLoading(true);
            }}
            onPlaybackStatusUpdate={handlePlaybackStatusUpdate}
          />
        )}
      </View>
      <View style={styles.suggestedWeightContainer}>
        <>
          <Text style={styles.suggestionText}>{recommendation?.text}</Text>
          <View
            style={[
              styles.suggestedStartingWeight,
              { backgroundColor: startingWeightAndColor.color ?? theme.BLACK },
            ]}
          >
            <Text style={styles.weightText}>{recommendation?.suggestedStartingWeight}</Text>
          </View>
        </>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  videoContainer: {
    flex: 1,
    position: 'relative',
    marginTop: defaultScale(15),
  },
  suggestedWeightContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    marginVertical: defaultScale(15),
  },
  suggestedStartingWeight: {
    width: defaultScale(220),
    height: defaultScale(45),
    alignItems: 'center',
    justifyContent: 'center',
    textTransform: 'uppercase',
  },
  suggestionText: {
    ...theme.textStyle.heading2,
    marginBottom: defaultScale(10),
  },
  weightText: {
    ...theme.textStyle.display1Bold,
    color: theme.WHITE,
    fontWeight: '800',
    fontSize: scaleText(25),
    textTransform: 'uppercase',
  },
  video: {
    width: '100%',
    height: '100%',
  },
  loadingContainer: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.BLACK,
  },
});
